import axios, {
  AxiosError,
  type AxiosResponse,
  type AxiosRequestConfig,
} from "axios";
import { Jsona } from "jsona";

import googleAnalytics from "./GoogleAnalyticsService";
import { store } from "../store";
import type { ItemCart } from "../interfaces/ItemCart";
import {
  VITE_API_ENDPOINT,
  SHIPPING_DELIVERY,
  SHIPPING_PICK_UP,
} from "~/config/constants";

const dataFormatter = new Jsona();

export class CartService {
  public async getCartInitData() {
    const translations = store.getters["translations/getSection"]("cart");
    const isLoading = store.getters["translations/hasLoading"]("cart");

    if (translations || isLoading) {
      return translations;
    }
    store.commit("translations/addLoading", "cart");
    const response = await axios.get("/eshop/cart/init-data");
    if (response) {
      if (
        response.data &&
        response.data.meta &&
        response.data.meta.translations
      ) {
        store.commit("translations/setSection", {
          cart: response.data.meta.translations,
        });
        store.commit("translations/removeLoading", "cart");
      }
      return response.data;
    }
  }

  public async addProduct(id: number, qty: number = 1) {
    let params = encodeURIComponent(id);
    const body = {};
    if (qty > 0) {
      params += "/" + encodeURIComponent(qty);
    }
    const response = await axios.get("/eshop/carrito/agregar/" + params, {
      params: body,
    });
    if (!(response instanceof AxiosError)) {
      store.dispatch("checkout/resetCheckout");
      return response.data;
    } else if (response.response) {
      return response.response.data;
    }
  }

  public async validateAddProduct(id: number, qty: number = 0) {
    let params = encodeURIComponent(id);
    const body = {};
    if (qty !== 0) {
      params += "/" + encodeURIComponent(qty);
    }
    const response = await axios.get("/eshop/carrito/valida/" + params, {
      params: body,
    });
    if (!(response instanceof AxiosError)) {
      return response.data;
    } else if (response.response) {
      return response.response.data;
    }
  }

  public async getTotalItems() {
    const response = await axios.get("/eshop/carrito/total-items");
    if (response && response.data) {
      if (response.data && response.data.meta) {
        return response.data.meta;
      }
      return response.data;
    }
  }

  public async getTotals() {
    const transactionId = store.getters["checkout/getTransactionId"];

    let body = {};
    let paymentMethodCode = store.getters["checkout/getPaymentMethodCode"];
    let paymentTokenizable = store.getters["checkout/getPaymentTokenizable"];
    let monthlyPlans = store.getters["checkout/getMonthlyPlans"];
    let isLastStep = store.getters["checkout/getIsLastStep"];

    if (transactionId) {
      paymentMethodCode =
        store.getters["checkoutTransaction/getPaymentMethodCode"];
      paymentTokenizable =
        store.getters["checkoutTransaction/getPaymentTokenizable"];
      isLastStep = store.getters["checkoutTransaction/getIsLastStep"];
      monthlyPlans = store.getters["checkoutTransaction/getMonthlyPlans"];
      const walletToUse = store.getters["transaction/getWalletToUse"];
      body = {
        ...body,
        transaction_id: transactionId,
        wallet_to_use: walletToUse,
      };
    }
    body = {
      ...body,
      monthly_payment_option_plans: monthlyPlans,
      payment_method_code: paymentMethodCode,
      tokenizableId: paymentTokenizable,
      is_last_step: isLastStep,
    };
    const response = await axios.get("/eshop/carrito/totales", {
      params: body,
    });
    if (response && response.data) {
      return response.data;
    }
  }

  public async cartDetails(resetOptions: boolean) {
    let body = {};
    const transactionId = store.getters["checkout/getTransactionId"];
    const couponCode = store.getters["cart/getCouponCode"];

    let trackingId = store.getters["checkout/getTrackingId"];
    let paymentMethodCode = store.getters["checkout/getPaymentMethodCode"];
    let paymentTokenizable = store.getters["checkout/getPaymentTokenizable"];
    let monthlyPlans = store.getters["checkout/getMonthlyPlans"];
    if (resetOptions != undefined) {
      body = {
        visited: encodeURIComponent(!resetOptions),
        coupon_code: couponCode,
      };
      if (transactionId) {
        trackingId = store.getters["checkoutTransaction/getTrackingId"];
        paymentMethodCode =
          store.getters["checkoutTransaction/getPaymentMethodCode"];
        paymentTokenizable =
          store.getters["checkoutTransaction/getPaymentTokenizable"];
        monthlyPlans = store.getters["checkoutTransaction/getMonthlyPlans"];
        const walletToUse = store.getters["transaction/getWalletToUse"];
        body = {
          ...body,
          transaction_id: transactionId,
          wallet_to_use: walletToUse,
        };
      }

      if (!resetOptions && paymentMethodCode && monthlyPlans) {
        body = {
          ...body,
          payment_method_code: paymentMethodCode,
          tokenizable_id: paymentTokenizable,
          monthly_payment_option_plans: monthlyPlans,
          tracking_id: trackingId,
        };
      }
    }
    const response = await axios.get("eshop/carrito/detalle", {
      params: body,
    });
    return response;
  }

  public async removeItem(productId: number) {
    const response = await axios.delete("eshop/carrito/quitar/" + productId);
    if (response) {
      store.dispatch("checkout/resetCheckout");
      if (response.data?.addedInfo) {
        googleAnalytics.addToCartEvent(response.data?.addedInfo);
      }
      return response.data;
    } else {
      console.log("errror al eliminar");
    }
  }

  public async clearCart() {
    const response = await axios.delete("eshop/carrito/clear-all");
    if (response) {
      store.dispatch("checkout/resetCheckout", true);
      return response.data;
    } else {
      console.log("errror al vaciar el carrito");
    }
  }

  public async updateQty(product: ItemCart, qty: number) {
    const response = await axios.post(
      "/eshop/carrito/actualizar/" + product.id + "/" + qty,
    );
    if (response) {
      store.dispatch("checkout/resetCheckout");
      return response.data;
    } else {
      console.log("Error al actualizar la cantidad del artículo");
    }
  }

  public async showAssembly(assemblyId: number | string) {
    const response = await axios.get(
      "/eshop/carrito/ensamble/" + encodeURIComponent(assemblyId),
    );
    if (!(response instanceof AxiosError)) {
      return response.data;
    } else if (response.response) {
      const resp: AxiosResponse = response.response;
      return resp.data;
    } else {
      return null;
    }
  }

  public async addAssemblyProduct(status: boolean) {
    const products = store.getters["assembly/getFormatedProducts"];
    const selectedAssemblyConfigId =
      store.getters["assembly/getSelectedAssemblyConfigId"];
    const body = {
      products: products,
      assembly_config_id: selectedAssemblyConfigId,
      requires_assembly: status,
    };
    const response = await axios.post("/eshop/carrito/ensamble", body);
    if (!(response instanceof AxiosError)) {
      const data = dataFormatter.deserialize(response.data);
      if (data && (data as unknown[]).length > 0) {
        store.dispatch("checkout/resetCheckout");
        store.commit(
          "assembly/setAssemblyId",
          (data as { assembly_id: string }[])[0].assembly_id,
        );
      }
      return response.data;
    } else if (response.response) {
      const resp: AxiosResponse = response.response;
      return resp.data;
    } else {
      return null;
    }
  }

  public async updateAssemblyQty(
    assemblyId: string | number,
    qty: number,
    products: Array<unknown> | null = null,
    _requiresAssembly: boolean = true,
  ) {
    const body = {
      type: "assemblies",
      id: assemblyId,
      qty: qty,
      requires_assembly: _requiresAssembly,
      products: products ? products : null,
    };
    const newJsonBody = dataFormatter.serialize({
      stuff: body,
    });
    const options: AxiosRequestConfig = {
      baseURL: VITE_API_ENDPOINT,
      headers: {
        Accept: "application/vnd.api+json",
        "Content-Type": "application/vnd.api+json",
      },
    };

    const response = await axios.patch(
      "/eshop/carrito/ensamble",
      newJsonBody,
      options,
    );
    if (!(response instanceof AxiosError)) {
      store.dispatch("checkout/resetCheckout");
      return response.data;
    } else if (response.response) {
      store.dispatch("checkout/resetCheckout");
      const resp: AxiosResponse = response.response;
      return resp.data;
    } else {
      console.log("Error al actualizar la cantidad del ensamble");
      return null;
    }
  }

  public async updateRequiresAssembly(assemblyId: number, status: boolean) {
    const body = {
      type: "assemblies",
      id: assemblyId,
      requires_assembly: status,
    };
    const newJsonBody = dataFormatter.serialize({
      stuff: body,
    });
    const options: AxiosRequestConfig = {
      baseURL: VITE_API_ENDPOINT,
      headers: {
        Accept: "application/vnd.api+json",
        "Content-Type": "application/vnd.api+json",
      },
    };

    const response = await axios.patch(
      "/eshop/carrito/ensamble",
      newJsonBody,
      options,
    );
    if (!(response instanceof AxiosError)) {
      store.dispatch("checkout/resetCheckout");
      return response.data;
    } else if (response.response) {
      store.dispatch("checkout/resetCheckout");
      const resp: AxiosResponse = response.response;
      return resp.data;
    } else {
      console.log("Error al actualizar la cantidad del ensamble");
      return null;
    }
  }

  public async deleteAssembly(assemblyId: number) {
    const options: AxiosRequestConfig = {
      baseURL: VITE_API_ENDPOINT,
      headers: {
        Accept: "application/vnd.api+json",
      },
    };

    const response = await axios.delete(
      "/eshop/carrito/ensamble/" + encodeURIComponent(assemblyId),
      options,
    );
    if (!(response instanceof AxiosError)) {
      return response.data;
    } else if (response.response) {
      store.dispatch("checkout/resetCheckout");
      const resp: AxiosResponse = response.response;
      return resp.data;
    } else {
      console.log("Error al eliminar el ensamble");
      return null;
    }
  }

  public async validateReview() {
    let body = {};
    const transactionId = store.getters["checkout/getTransactionId"];
    let paymentMethodCode = store.getters["checkout/getPaymentMethodCode"];
    let paymentTokenizable = store.getters["checkout/getPaymentTokenizable"];
    let monthlyPlans = store.getters["checkout/getMonthlyPlans"];
    let trackingId = store.getters["checkout/getTrackingId"];
    let shippingType = store.getters["checkout/getIsShipping"]
      ? SHIPPING_DELIVERY
      : SHIPPING_PICK_UP;

    if (transactionId) {
      paymentMethodCode =
        store.getters["checkoutTransaction/getPaymentMethodCode"];
      paymentTokenizable =
        store.getters["checkoutTransaction/getPaymentTokenizable"];
      monthlyPlans = store.getters["checkoutTransaction/getMonthlyPlans"];
      trackingId = store.getters["checkoutTransaction/getTrackingId"];
      shippingType = store.getters["checkoutTransaction/getIsShipping"]
        ? SHIPPING_DELIVERY
        : SHIPPING_PICK_UP;
    }

    if (paymentMethodCode && monthlyPlans) {
      body = {
        payment_method_code: paymentMethodCode,
        tokenizable_id: paymentTokenizable,
        monthly_payment_option_plans: monthlyPlans,
        shipping_type: shippingType,
        tracking_id: trackingId,
      };
    }
    if (transactionId) {
      body = {
        ...body,
        transaction_id: transactionId,
      };
    }
    const response = await axios.get("eshop/carrito/validar-revision", {
      params: body,
    });
    if (!(response instanceof AxiosError)) {
      return response.data;
    } else if (response.response) {
      const resp: AxiosResponse = response.response;
      return resp.data;
    } else {
      return null;
    }
  }

  public async addCoupon(couponCode: string) {
    const body = {
      coupon_code: couponCode,
      payment_method_code: store.getters["checkout/getPaymentMethodCode"],
    };
    const response = await axios.post("/eshop/carrito/agregar-cupon", body);
    if (!(response instanceof AxiosError)) {
      return response.data;
    } else if (response.response) {
      const resp: AxiosResponse = response.response;
      return resp.data;
    } else {
      return null;
    }
  }

  public async deleteCoupon() {
    const response = await axios.post("/eshop/carrito/eliminar-cupon");
    if (!(response instanceof AxiosError)) {
      return response;
    } else if (response.response) {
      const resp: AxiosResponse = response.response;
      return resp;
    } else {
      console.log("Error al eliminar el cupón");
      return null;
    }
  }

  public async addWallet(amount: number) {
    const transactionId = store.getters["checkout/getTransactionId"];
    let body: { [key: string]: number | string } = {
      wallet_to_use: amount,
    };
    if (transactionId) {
      body = {
        ...body,
        transaction_id: transactionId,
      };
    }
    const response = await axios.post("/eshop/carrito/agregar-monedero", body);
    if (!(response instanceof AxiosError)) {
      return response.data;
    } else if (response.response) {
      const resp: AxiosResponse = response.response;
      return resp.data;
    } else {
      return null;
    }
  }

  public async deleteWallet() {
    const response = await axios.post("/eshop/carrito/eliminar-monedero");
    if (!(response instanceof AxiosError)) {
      return response;
    } else if (response.response) {
      const resp: AxiosResponse = response.response;
      return resp;
    } else {
      console.log("Error al eliminar el cupón");
      return null;
    }
  }

  async senCodeToUseWallet() {
    const response = await axios.post(
      "/eshop/carrito/enviar-codigo-para-usar-monedero",
    );
    return response.data;
  }

  async verifyCodeToUseWallet(verification_code: number) {
    const data = {
      verification_code: verification_code,
    };
    const response = await axios.post(
      "/eshop/carrito/verificar-codigo-para-usar-monedero",
      data,
    );
    return response.data;
  }

  public async createPreference() {
    const transactionId = store.getters["checkout/getTransactionId"];
    let paymentMethodCode = store.getters["checkout/getPaymentMethodCode"];
    let deviceSessionId = store.getters["checkout/getDeviceSessionId"];
    const trackingId = store.getters["checkout/getTrackingId"];
    const cart = store.getters["cart/getCart"];
    const isShipping = store.getters["checkout/getIsShipping"];
    const customerAddress = store.getters["checkout/getCustomerAddress"];

    let finalDeviceSessionId = null;
    if (paymentMethodCode.toLowerCase().includes("openpay")) {
      if (!deviceSessionId) {
        store.dispatch("checkout/resetDeviceSessionId");
        deviceSessionId = store.getters["checkout/getDeviceSessionId"];
      }
      finalDeviceSessionId = deviceSessionId;
    } else if (paymentMethodCode.toLowerCase().includes("paypal")) {
      finalDeviceSessionId = trackingId;
    } else if (paymentMethodCode.toLowerCase().includes("mercado_pago")) {
      if (!deviceSessionId) {
        await store.dispatch("checkout/resetDeviceSessionId");
        deviceSessionId = store.getters["checkout/getDeviceSessionId"];
      }
      finalDeviceSessionId = deviceSessionId;
    }

    if (transactionId) {
      paymentMethodCode =
        store.getters["checkoutTransaction/getPaymentMethodCode"];
    }
    const body = {
      transaction_id: transactionId,
      payment_method_code: paymentMethodCode,
      payment_device_session_id: finalDeviceSessionId
        ? finalDeviceSessionId
        : null,
      shipping: cart.shipping,
      shipping_type: isShipping ? SHIPPING_DELIVERY : SHIPPING_PICK_UP,
      delivery_address_id: customerAddress ? customerAddress.id : null,
    };
    const response = await axios.post("/eshop/carrito/crear-preferencia", body);
    return response;
  }
}
